/* =============================================================================
 * FilterNumberField — shared filter primitive (KGH-795)
 * -----------------------------------------------------------------------------
 * A single numeric bound inside a FilterSection: "Minimum GPA", "Minimum SAT".
 * The athlete side expresses every numeric filter as a two-ended RangeSlider,
 * but several coach filters are genuinely one-sided — a coach sets a floor and
 * has no ceiling — and a slider pinned at one end would misrepresent that.
 *
 * It matches the search input in FilterSearchableList exactly (same padding,
 * same border, same focus behaviour) because they share a sidebar, and it uses
 * the --aps-control-* tokens for the same reason those exist.
 *
 * ── [55] The field a coach types in is NOT the field the grid reads ──────────
 * This used to be a controlled <input type="number"> wired straight to the URL:
 * every keystroke rewrote the query string, the page normalised it, and the
 * normalised string came back as the input's value mid-word. Three things
 * followed, all of them reproduced on staging:
 *
 *   - Chrome reports an empty string for a half-typed number ("3." is not a
 *     valid number), so the dot was dropped on its way out and "3.5" typed over
 *     a settled "3.20" landed as "35" (2 runs in 3) or "0.5" (1 in 3). The
 *     coach then filtered on a GPA they never typed — and 35 is outside the
 *     server's 0–4.5 range, so the grid answered INVALID_FILTER.
 *   - A normalised value written back into a focused field moves the caret.
 *   - The grid could be one keystroke behind the chip beside it.
 *
 * So the field now owns a draft while it has focus and never takes a value back
 * from above while the coach is typing. It emits upward only what the caller can
 * actually use: empty, or a number inside [min, max], debounced so the grid is
 * not re-queried per keystroke. Anything else is held — with a line under the
 * field saying why nothing moved — until blur, which clamps, normalises and
 * commits. The value a coach sees is always the value the grid is using.
 * ========================================================================== */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { TextInput } from '../ui';

/** Debounce: long enough to hold a whole number, short enough to feel live. */
const COMMIT_DELAY_MS = 300;

export interface FilterNumberFieldProps {
  label: string;
  /** Kept as a string so a half-typed "3." does not snap back while typing. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Rendered after the label, e.g. "4.0 scale". */
  hint?: string;
  /** Anything below the field — a note about what the value also affects. */
  children?: React.ReactNode;
}

/** Digits and at most one decimal point. Anything else never reaches state. */
function sanitiseKeystrokes(raw: string): string {
  const cleaned = raw.replace(',', '.').replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}

/** "3." → "3", ".5" → "0.5", "" → "". Spelled the way a coach reads it back. */
export function normaliseNumericDraft(raw: string): string {
  const v = raw.trim();
  if (!v || v === '.') return '';
  if (/^\.\d+$/.test(v)) return `0${v}`;
  if (/^\d+\.$/.test(v)) return v.slice(0, -1);
  return v;
}

type Bounds = { min?: number; max?: number };

/** '' | a number inside the bounds | null when it is neither (yet). */
function commitValue(draft: string, { min, max }: Bounds): string | null {
  const normalised = normaliseNumericDraft(draft);
  if (!normalised) return '';
  const n = Number(normalised);
  if (!Number.isFinite(n)) return null;
  if (min !== undefined && n < min) return null;
  if (max !== undefined && n > max) return null;
  return normalised;
}

/** Blur is the last word: whatever is in the field becomes a usable value. */
function clampValue(draft: string, { min, max }: Bounds): string {
  const normalised = normaliseNumericDraft(draft);
  if (!normalised) return '';
  const n = Number(normalised);
  if (!Number.isFinite(n)) return '';
  if (min !== undefined && n < min) return String(min);
  if (max !== undefined && n > max) return String(max);
  return normalised;
}

function rangeNote(min?: number, max?: number): string {
  if (min !== undefined && max !== undefined) return `Enter a number between ${min} and ${max}.`;
  if (max !== undefined) return `Enter a number no higher than ${max}.`;
  if (min !== undefined) return `Enter a number of at least ${min}.`;
  return 'Enter a number.';
}

export const FilterNumberField: React.FC<FilterNumberFieldProps> = ({
  label,
  value,
  onChange,
  placeholder,
  min,
  max,
  step,
  hint,
  children,
}) => {
  const id = useId();
  const noteId = `${id}-note`;
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /* The last string this field handed upward. The value coming back down is
     that string after the page has normalised it, and adopting it while the
     coach is mid-word is exactly the bug this file exists to prevent. */
  const emitted = useRef(value);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };
  useEffect(() => clearTimer, []);

  /* A value changed from somewhere else — a chip removed, Clear all, Back, the
     saved recruiting profile applied — still has to reach the field. Only when
     the coach is not typing in it. */
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    if (focused) return;
    if (value === emitted.current) return;
    emitted.current = value;
    setDraft(value);
  }, [value, focused]);

  /* onChange through a ref: a debounced commit must call the CURRENT handler,
     and must compare against what this field last emitted (the ref) rather than
     a `value` captured when the keystroke happened. */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const emit = useCallback((next: string) => {
    if (next === emitted.current) return;
    emitted.current = next;
    onChangeRef.current(next);
  }, []);

  const handleChange = (raw: string) => {
    const next = sanitiseKeystrokes(raw);
    setDraft(next);
    clearTimer();
    const committable = commitValue(next, { min, max });
    /* null = not a usable filter yet ("3.", "35" on a 4.0 scale). The grid
       holds its last answer rather than being sent a query the server refuses. */
    if (committable === null) return;
    timer.current = setTimeout(() => emit(committable), COMMIT_DELAY_MS);
  };

  const handleBlur = () => {
    setFocused(false);
    clearTimer();
    /* Something else changed this filter while the field had focus (Clear all,
       a chip removed, the saved profile applied). That is the newer answer —
       committing the draft here would quietly undo it. */
    if (valueRef.current !== emitted.current) {
      emitted.current = valueRef.current;
      setDraft(valueRef.current);
      return;
    }
    const settled = clampValue(draft, { min, max });
    setDraft(settled);
    emit(settled);
  };

  /* Only a number ABOVE the ceiling is worth saying out loud: it can only be a
     mistake ("35" in a 4.0 GPA field). A number below the floor is almost
     always an unfinished one — the "1" of a 1200 SAT — and flashing an error at
     every digit of a slowly typed number is noise. Blur clamps either way. */
  const tooBig = (() => {
    const n = Number(normaliseNumericDraft(draft));
    return max !== undefined && normaliseNumericDraft(draft) !== '' && Number.isFinite(n) && n > max;
  })();
  const outOfRange = tooBig;

  return (
    <div style={{ marginBottom: '10px' }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 500,
          color: 'var(--aps-control-label)',
          marginBottom: '4px',
        }}
      >
        {label}
        {hint && (
          <span style={{ color: 'var(--aps-control-muted)', fontWeight: 400 }}> ({hint})</span>
        )}
      </label>
      {/* TextInput, keeping the sidebar's compact 8/12 padding rather than the
          form-column 12/14, and its own danger border driven by `invalid`. The
          focus/blur border colouring went with the inline `outline: none`
          beside it, which outranked the global focus ring and left this box
          drawing nothing on keyboard focus. The ring is the indicator now; the
          border no longer tints, because the primitive's inline `border` beats
          `.aps-input:focus` (globals.css documents that at the rule). The focus
          STATE is still tracked — it is what stops a normalised value coming
          back down mid-word. */}
      <TextInput
        id={id}
        /* text, not number: Chrome reports "" for a half-typed number, which is
           how the decimal point used to get lost between the field and the URL.
           inputMode keeps the numeric keypad on a phone. */
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={draft}
        aria-describedby={outOfRange ? noteId : undefined}
        invalid={outOfRange}
        data-min={min}
        data-max={max}
        data-step={step}
        placeholder={placeholder}
        onChange={(e) => handleChange(e.target.value)}
        style={{
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '13px',
          borderColor: outOfRange ? 'var(--aps-danger)' : 'var(--aps-control-border)',
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
      />
      {outOfRange && (
        <p id={noteId} style={{ margin: '4px 0 0', fontSize: 12, lineHeight: 1.4, color: 'var(--aps-danger)' }}>
          {rangeNote(min, max)}
        </p>
      )}
      {children}
    </div>
  );
};

export default FilterNumberField;
